import { useState, useMemo } from 'react';
import { useCategories, PRESET_COLORS, PRESET_ICONS } from '../../../hooks/useCategories';

interface Props {
  transactionType: 'receita' | 'despesa';
  selected: string;
  onSelect: (name: string) => void;
  subcategory: string;
  onSubcategoryChange: (value: string) => void;
  /** Category suggested from payee history — will be highlighted */
  suggested?: string;
}

const MAX_VISIBLE = 6;

export const CategoryPicker = ({
  transactionType,
  selected,
  onSelect,
  subcategory,
  onSubcategoryChange,
  suggested,
}: Props) => {
  const categoryType = transactionType === 'receita' ? 'income' : 'expense';
  const { categories, loading, createCategory } = useCategories(categoryType);

  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#22c55e');
  const [newIcon, setNewIcon] = useState('ri-tag-line');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return categories;
    return categories.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));
  }, [categories, search]);

  // Visible: suggested first, then others. Collapse to MAX_VISIBLE unless showAll.
  const sorted = useMemo(() => {
    if (!suggested) return filtered;
    return [
      ...filtered.filter(c => c.name === suggested),
      ...filtered.filter(c => c.name !== suggested),
    ];
  }, [filtered, suggested]);

  const visible = (showAll || search.trim()) ? sorted : sorted.slice(0, MAX_VISIBLE);
  const hasMore = !search.trim() && sorted.length > MAX_VISIBLE;

  const handleCreate = async () => {
    if (!newName.trim()) { setFormError('Informe um nome.'); return; }
    setCreating(true);
    setFormError('');
    const result = await createCategory({ name: newName.trim(), type: categoryType, color: newColor, icon: newIcon });
    setCreating(false);
    if (result.success) {
      onSelect(newName.trim());
      setNewName('');
      setShowForm(false);
    } else {
      setFormError('Erro ao criar categoria. Tente novamente.');
    }
  };

  return (
    <div className="space-y-2">
      {/* Search — only shown when showAll or typing */}
      {(showAll || search.trim()) && (
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar categoria..."
            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 bg-gray-50"
          />
        </div>
      )}

      {loading ? (
        <div className="flex gap-1.5 flex-wrap">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-8 w-24 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {visible.map((cat) => {
              const isSelected = selected === cat.name;
              const isSuggested = suggested === cat.name && !isSelected;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => onSelect(isSelected ? '' : cat.name)}
                  style={isSelected ? { borderColor: cat.color, backgroundColor: cat.color + '20', color: cat.color } : undefined}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-xs font-600 transition-all cursor-pointer whitespace-nowrap relative ${
                    isSelected
                      ? 'border-current'
                      : isSuggested
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {cat.icon && (
                    <i className={`${cat.icon} text-sm`} style={{ color: isSelected ? cat.color : isSuggested ? '#059669' : undefined }} />
                  )}
                  {cat.name}
                  {isSuggested && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-400 rounded-full" title="Sugerida" />
                  )}
                </button>
              );
            })}

            {/* Ver todas / Menos */}
            {hasMore && !showForm && (
              <button
                type="button"
                onClick={() => setShowAll(v => !v)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl border border-dashed border-gray-300 text-xs font-600 text-gray-400 hover:border-emerald-400 hover:text-emerald-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className={`text-sm ${showAll ? 'ri-arrow-up-s-line' : 'ri-more-line'}`} />
                {showAll ? 'Ver menos' : `+${sorted.length - MAX_VISIBLE} mais`}
              </button>
            )}

            {/* Nova categoria */}
            {!showForm && (
              <button
                type="button"
                onClick={() => { setShowForm(true); setFormError(''); setShowAll(false); setSearch(''); }}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl border border-dashed border-gray-300 text-xs font-600 text-gray-400 hover:border-emerald-400 hover:text-emerald-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-sm" />Nova
              </button>
            )}
          </div>

          {/* Empty search result */}
          {search.trim() && filtered.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-2">Nenhuma categoria encontrada</p>
          )}
        </>
      )}

      {/* Subcategory input */}
      {selected && !showForm && (
        <input
          value={subcategory}
          onChange={(e) => onSubcategoryChange(e.target.value)}
          placeholder="Subcategoria: ex: Delivery, Uber, Streaming..."
          className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:border-emerald-400 bg-gray-50 text-gray-600"
        />
      )}

      {/* Inline new category form */}
      {showForm && (
        <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-700 text-gray-700">Nova categoria</span>
            <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
              <i className="ri-close-line text-sm" />
            </button>
          </div>
          <div>
            <input
              value={newName}
              onChange={(e) => { setNewName(e.target.value); setFormError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="Nome da categoria"
              className={`w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:border-emerald-400 bg-white ${formError ? 'border-red-400' : 'border-gray-200'}`}
            />
            {formError && <p className="text-xs text-red-500 mt-1">{formError}</p>}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-500 shrink-0">Cor:</span>
            <div className="flex gap-1.5 flex-wrap">
              {PRESET_COLORS.slice(0, 8).map((c) => (
                <button key={c} type="button" onClick={() => setNewColor(c)}
                  style={{ backgroundColor: c }}
                  className={`w-5 h-5 rounded-full cursor-pointer transition-all shrink-0 ${newColor === c ? 'ring-2 ring-offset-1 ring-gray-500 scale-110' : ''}`}
                />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-500 shrink-0">Ícone:</span>
            <div className="flex gap-1 flex-wrap">
              {PRESET_ICONS.slice(0, 8).map((icon) => (
                <button key={icon} type="button" onClick={() => setNewIcon(icon)}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all cursor-pointer text-sm ${newIcon === icon ? 'bg-emerald-100 text-emerald-600' : 'bg-white border border-gray-200 text-gray-500 hover:border-gray-300'}`}
                >
                  <i className={icon} />
                </button>
              ))}
            </div>
          </div>
          <button type="button" onClick={handleCreate} disabled={!newName.trim() || creating}
            className="w-full py-2 text-xs font-600 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-xl cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5">
            {creating && <i className="ri-loader-4-line animate-spin" />}
            {creating ? 'Criando...' : 'Criar e selecionar'}
          </button>
        </div>
      )}
    </div>
  );
};
